/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * LeaderboardPage — /results/:eventActivityId
 *
 * The motion board: one race's results. Public, so a visitor with the link sees
 * the field without signing in; a signed-in athlete also gets their own row
 * pinned to the foot of the table.
 *
 * Direction A from the design canvas, built bottom-up: this is the field and
 * the tabs. The podium, the hero and the personal band read PUBLISHED awards,
 * which do not exist until the publish phase, so they are not here yet.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useFeedUser } from '../useFeedData';
// Shared with the index, so the app token is minted once — see boardClient.
import boardClient from './boardClient';
// The other activities of the same event, so the board is a way into all of
// them rather than a dead end on one. Public reads, same as the board itself.
import { fetchEventActivities } from './boardIndexApi';
import { idOf } from './boardIndexModel';
import {
  fetchWholeTab, fetchStatusCounts, fetchMyResultForRace, fetchFieldSummary, countsFromSummary,
  fetchPodium, fetchFinishBands,
} from './boardApi';
// Attaching happens here, on the board, not in a separate organizer section —
// and the panel draws nothing unless the viewer created this race. See its own
// header: the server decides, not this page.
import OrganizerAttachPanel from './OrganizerAttachPanel';
import OrganizerBoardLink from './OrganizerBoardLink';
// Switching tabs used to show the words "Loading results…", which read as the
// page having stopped. This keeps the table's shape on screen and moves.
import BoardLoader from './BoardLoader';
import {
  TABS, DEFAULT_TAB, visibleTabs, sortingAllowedOn, defaultSortFor, initialDirectionFor,
  emptyCopyFor, formatClock, formatKm, formatPace, sortRows, percentBehind,
  histogramBars, HISTOGRAM_MIN_FINISHERS,
} from './boardModel';

const apiHelper = boardClient;

/** How long each podium card holds the phone screen before the next slides in. */
const PODIUM_DWELL_MS = 3600;

/** Below this the podium is a swipe rail; above it, the three-across podium. */
const PHONE = '(max-width: 760px)';

/**
 * The podium rail that turns itself, on phones only.
 *
 * WHY A HOOK AND NOT CSS. A scroll-snap rail gives the swipe for free, but
 * nothing in CSS can advance it, pause it on a touch or draw a timer. The
 * three things have to move together or the bar lies about when the card
 * turns.
 *
 * IT STOPS WHEN TOUCHED, and that is the whole point of it. Hover, a drag, or
 * the keyboard reaching a card all hold it, because a card that slides away
 * mid-read is worse than one that never moved.
 *
 * REDUCED MOTION GETS NO ROTATION AT ALL. A carousel is the one thing a person
 * cannot opt out of by simply not touching it, so when the phone asks for less
 * motion the rail sits still, the bar sits full on the current card, and the
 * cards are reached by swiping. This is also why the rotation must never be the
 * ONLY way to see second and third.
 *
 * The hook is inert on desktop: `enabled` is the media query, so the effect
 * tears down the moment the layout goes back to three-across.
 */
function usePodiumRotation() {
  // A CALLBACK REF, NOT useRef. The podium only renders once BOTH the awards
  // and the results have arrived, which is later than the last time the deps
  // of this effect change — so with a plain ref the effect ran while
  // `railRef.current` was still null, bailed out, and never ran again. The
  // rail then sat there swipeable but dead. Setting state when the node
  // attaches is what re-runs the effect at the only moment it can work.
  const [rail, setRail] = useState(null);
  const railRef = useCallback((node) => setRail(node), []);
  const [onPhone, setOnPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(PHONE).matches : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(PHONE);
    const sync = () => setOnPhone(mq.matches);
    sync();
    // addListener is the old name, still the only one some in-app browsers have.
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    if (!rail || !onPhone) return undefined;

    const cards = Array.from(rail.children);
    const fills = cards.map((c) => c.querySelector('[data-podium-fill]'));
    // One winner is not a carousel, and two are reached in a single swipe.
    if (cards.length < 2) return undefined;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = 0;
    let elapsed = 0;
    let held = false;
    let last = 0;
    let frame = 0;

    const hold = (on) => { held = on; };
    const slideTo = (n) => {
      index = ((n % cards.length) + cards.length) % cards.length;
      rail.scrollTo({
        left: cards[index].offsetLeft - cards[0].offsetLeft,
        behavior: still ? 'auto' : 'smooth',
      });
    };

    // A swipe of their own takes over: whatever card they land on becomes the
    // current one and its timer starts again from nothing, so it never turns
    // the instant they let go.
    const onScroll = () => {
      const x = rail.scrollLeft + cards[0].offsetLeft;
      let best = 0;
      let near = Infinity;
      cards.forEach((c, n) => {
        const d = Math.abs(c.offsetLeft - x);
        if (d < near) { near = d; best = n; }
      });
      if (best !== index) { index = best; elapsed = 0; }
    };

    const tick = (t) => {
      if (!last) last = t;
      const dt = t - last;
      last = t;
      if (!held && !still) {
        elapsed += dt;
        if (elapsed >= PODIUM_DWELL_MS) { elapsed = 0; slideTo(index + 1); }
      }
      const ratio = still ? 1 : Math.min(elapsed / PODIUM_DWELL_MS, 1);
      fills.forEach((f, n) => {
        if (f) f.style.transform = `scaleX(${n === index ? ratio : 0})`;
      });
      frame = window.requestAnimationFrame(tick);
    };

    const enter = () => hold(true);
    const leave = () => hold(false);
    rail.addEventListener('mouseenter', enter);
    rail.addEventListener('mouseleave', leave);
    rail.addEventListener('pointerdown', enter);
    rail.addEventListener('focusin', enter);
    rail.addEventListener('focusout', leave);
    window.addEventListener('pointerup', leave);
    window.addEventListener('pointercancel', leave);
    rail.addEventListener('scroll', onScroll, { passive: true });
    frame = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frame);
      rail.removeEventListener('mouseenter', enter);
      rail.removeEventListener('mouseleave', leave);
      rail.removeEventListener('pointerdown', enter);
      rail.removeEventListener('focusin', enter);
      rail.removeEventListener('focusout', leave);
      window.removeEventListener('pointerup', leave);
      window.removeEventListener('pointercancel', leave);
      rail.removeEventListener('scroll', onScroll);
      // Leaving the phone layout must not leave a half-drawn bar behind.
      fills.forEach((f) => { if (f) f.style.transform = 'scaleX(0)'; });
    };
  }, [rail, onPhone]);

  return { railRef, onPhone };
}

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
const HAIR = 'rgba(36, 23, 8, .10)';
const ROWS_PER_PAGE = 100;

/** The podium is named rather than given a percentage. */
const PODIUM_WORD = { 1: 'Winner', 2: 'Runner-up', 3: 'Third place' };

const th = {
  textAlign: 'left', padding: '14px 14px', fontSize: 11, fontWeight: 600,
  letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED,
  borderBottom: `1px solid ${HAIR}`, whiteSpace: 'nowrap',
};
const td = { padding: '0 14px', height: 58, borderBottom: '1px solid rgba(36,23,8,.07)' };
const mono = { fontVariantNumeric: 'tabular-nums' };

/** "Priya Sharma" -> "PS". One letter when there is only one word. */
function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/**
 * The shape of the field: how many finishers landed in each slice of time.
 *
 * Drawn as plain boxes rather than a charting library — the board already
 * loads on a phone over event wifi, and one more bundle to show thirty
 * rectangles is a poor trade. Every bar carries its own text in `title` and in
 * the visually-hidden list beneath it, so the shape is available to a reader
 * who cannot see it as well as to one who can.
 */
function FinishSpread({ bars, mineResult, summary }) {
  if (!bars || bars.length === 0) return null;
  const busiest = bars.reduce((best, b) => (b.runners > best.runners ? b : best), bars[0]);
  const mine = bars.find((b) => b.mine);
  const ahead = mineResult && summary
    ? percentBehind(mineResult.position_overall, summary.completed)
    : null;

  // Five labels along the foot, evenly spaced across the bands — the design's
  // axis. With fewer bars than labels it simply repeats the nearest band.
  const marks = [0, 0.25, 0.5, 0.75, 1]
    .map((at) => bars[Math.min(bars.length - 1, Math.round(at * (bars.length - 1)))])
    .filter(Boolean);

  return (
    <section
      aria-label="Spread of finish times"
      style={{
        background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 22,
        padding: '26px 28px', marginBottom: 20, position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-.016em', color: INK }}>
          {ahead !== null ? (
            <>You finished ahead of <span style={{ color: ACCENT }}>{ahead}%</span> of the field</>
          ) : 'How the field finished'}
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: MUTED }}>
          Every finish time, in five-minute bands
          {mine && <> — yours is the {formatClock(mine.startSeconds)} band</>}
        </p>
      </div>

      <div aria-hidden="true" style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 118, marginTop: 22 }}>
        {bars.map((bar) => (
          <div
            key={bar.startSeconds}
            title={`${formatClock(bar.startSeconds)}\u2013${formatClock(bar.endSeconds)} \u00b7 ${bar.runners} ${bar.runners === 1 ? 'finisher' : 'finishers'}`}
            // minWidth 0 matters: without it a flex child will not shrink below
            // its content, the YOU label sets a floor, and twenty-two of those
            // pushed the whole chart wider than its card on a phone.
            style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 6, height: '100%' }}
          >
            {/* The label exists on every bar so they all start at the same
                height, but only YOUR bar carries the word. Rendering it on all
                of them and hiding it with opacity left "YOU YOU YOU…" in the
                page's text, which is what a screen reader reads out. */}
            <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT, whiteSpace: 'nowrap', minHeight: 13 }}>
              {bar.mine ? 'YOU' : '\u00a0'}
            </span>
            <div
              style={{
                width: '100%',
                // A bar that is there but short must still be visible, or an
                // empty slot and a lone finisher look identical.
                height: bar.runners > 0 ? `${Math.max(6, bar.share * 100)}%` : 2,
                borderRadius: '4px 4px 2px 2px',
                background: bar.mine ? ACCENT : (bar.runners > 0 ? 'rgba(255,106,26,.22)' : HAIR),
              }}
            />
          </div>
        ))}
      </div>

      <div style={{ ...mono, display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 11, fontWeight: 500, color: MUTED }}>
        {marks.map((bar, i) => <span key={i}>{formatClock(bar.startSeconds)}</span>)}
      </div>

      {/* The same shape, for a reader who cannot see it. */}
      <ul style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {bars.filter((b) => b.runners > 0).map((bar) => (
          <li key={bar.startSeconds}>
            {formatClock(bar.startSeconds)} to {formatClock(bar.endSeconds)}: {bar.runners}
            {bar.runners === 1 ? ' finisher' : ' finishers'}{bar.mine ? ', including you' : ''}
          </li>
        ))}
        {busiest.runners > 1 && <li>Most finishers came in around {formatClock(busiest.startSeconds)}.</li>}
      </ul>
    </section>
  );
}

export default function LeaderboardPage() {
  const { eventActivityId } = useParams();
  const raceId = Number(eventActivityId);
  const user = useFeedUser();

  const [tabKey, setTabKey] = useState(DEFAULT_TAB);
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);

  const tab = TABS.find((t) => t.key === tabKey) || TABS[0];

  // One saved query carries every tab count AND the median, which no ordinary
  // read can give: a median needs every finish time sorted, not one page of
  // them. If that query is not seeded on this server the counts still work —
  // they fall back to one small read per tab, since every read answers with a
  // total anyway.
  const counts = useQuery({
    queryKey: ['board', 'summary', raceId],
    queryFn: async ({ signal }) => {
      try {
        const row = await fetchFieldSummary(apiHelper, { eventActivityId: raceId, signal });
        return { counts: countsFromSummary(row), summary: row };
      } catch (e) {
        const tallies = await fetchStatusCounts(apiHelper, { eventActivityId: raceId, tabs: TABS, signal });
        return { counts: tallies, summary: null };
      }
    },
    enabled: Number.isFinite(raceId),
    // One retry, as with the rows. The default three held the title in its
    // skeleton long after the finishers had arrived — the page looked stuck on
    // its own name while showing a complete table underneath.
    retry: 1,
  });
  const tabCounts = counts.data?.counts || {};
  const summary = counts.data?.summary || null;

  const results = useQuery({
    queryKey: ['board', 'rows', raceId, tabKey, term],
    queryFn: ({ signal }) => fetchWholeTab(apiHelper, {
      eventActivityId: raceId, statuses: tab.statuses, search: term || undefined, signal,
    }),
    enabled: Number.isFinite(raceId),
    // One retry, not the default three. Three kept the page "loading" for the
    // best part of ten seconds before it would admit anything was wrong, and a
    // visitor reads that as a hang rather than a failure.
    retry: 1,
  });

  const podium = useQuery({
    queryKey: ['board', 'podium', raceId],
    queryFn: ({ signal }) => fetchPodium(apiHelper, { eventActivityId: raceId, signal }),
    enabled: Number.isFinite(raceId),
  });
  // Every race, so this one's siblings can be found. One cached read shared
  // with the /results index — moving between boards does not re-ask.
  const races = useQuery({
    queryKey: ['board-index', 'event-activities'],
    queryFn: ({ signal }) => fetchEventActivities(apiHelper, { signal }),
    staleTime: 120000,
  });

  const bands = useQuery({
    queryKey: ['board', 'bands', raceId],
    queryFn: ({ signal }) => fetchFinishBands(apiHelper, { eventActivityId: raceId, signal }),
    enabled: Number.isFinite(raceId),
  });
  const mine = useQuery({
    queryKey: ['board', 'mine', raceId, user.userId],
    queryFn: ({ signal }) => fetchMyResultForRace(apiHelper, {
      userId: user.userId, eventActivityId: raceId, signal,
    }),
    enabled: Number.isFinite(raceId) && user.userId !== null,
  });

  // The default follows the race: time for a set distance, distance for an open
  // challenge. Until a heading is clicked, that is what the board is sorted by.
  const activeSort = sort || defaultSortFor({ total_distance: 1 });
  const canSort = sortingAllowedOn(tabKey);

  const rows = results.data?.rows || [];
  const sorted = useMemo(
    () => (canSort ? sortRows(rows, activeSort.column, activeSort.direction) : rows),
    [rows, canSort, activeSort.column, activeSort.direction],
  );
  const pageCount = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const visible = sorted.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

  // This race's event, then its siblings in running order.
  const siblings = useMemo(() => {
    const all = races.data?.races || [];
    const here = all.find((r) => String(r.id) === String(raceId));
    const eventId = here ? idOf(here.event_id) : null;
    if (eventId === null || eventId === undefined) return [];
    return all
      .filter((r) => String(idOf(r.event_id)) === String(eventId))
      .sort((a, b) => Number(a.order_index ?? a.id) - Number(b.order_index ?? b.id));
  }, [races.data, raceId]);

  // On a phone the podium is a rail that turns itself; on desktop this is
  // inert and the three cards stand as they always have.
  const { railRef } = usePodiumRotation();

  // The podium, with each winner's own result joined back on so the card can
  // carry a time and a pace. The award table freezes names, not figures.
  const podiumCards = useMemo(() => {
    const byResult = new Map((results.data?.rows || []).map((r) => [String(r.id), r]));
    return (podium.data || [])
      .map((award) => ({ award, result: byResult.get(String(idOf(award.event_result_id))) || null }))
      .sort((a, b) => Number(a.award.position) - Number(b.award.position));
  }, [podium.data, results.data]);

  // How much of the field is behind you, or null when the race is not ranked.
  const ahead = mine.data && summary
    ? percentBehind(mine.data.position_overall, summary.completed)
    : null;

  const tabs = visibleTabs(tabCounts);
  const empty = emptyCopyFor(tabKey);

  function pickTab(key) {
    setTabKey(key);
    setPage(1);
    setSort(null);
  }

  function pickSort(column) {
    setPage(1);
    setSort((current) => (current && current.column === column
      ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { column, direction: initialDirectionFor(column) }));
  }

  function onSearch(event) {
    event.preventDefault();
    setTerm(search.trim());
    setPage(1);
  }

  if (!Number.isFinite(raceId)) {
    return <main style={{ padding: 32 }}><p style={{ color: MUTED }}>That race link is not valid.</p></main>;
  }

  return (
    <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 72px' }}>
      <style>
        {`.mb-tabstrip::-webkit-scrollbar{display:none}
          @keyframes mb-skel-sweep { from { background-position: -240px 0 } to { background-position: 460px 0 } }
          .mb-skel {
            background-color: rgba(250,243,231,.10);
            background-image: linear-gradient(90deg, rgba(250,243,231,0) 0%, rgba(250,243,231,.20) 45%, rgba(250,243,231,0) 70%);
            background-size: 400px 100%; background-repeat: no-repeat;
            animation: mb-skel-sweep 1.3s linear infinite;
          }
          @media (prefers-reduced-motion: reduce) { .mb-skel { animation: none } }
          .mb-podium-track { display: none }
          @media (max-width: 760px) {
            /* The pack is a tall figure. At 390px it stood directly behind the
               figures and the two fought: the numbers were unreadable and the
               runners looked like a smudge. It now sits well back, cropped to
               the right edge, as texture rather than subject. */
            /* !important on purpose: these elements carry inline styles, which
               otherwise beat anything a stylesheet says, media query or not. */
            /* Visible, but behind the words. At .20 it read as a smudge; the
               pack is the picture, so it keeps its presence — it simply sits
               to the right of where the type lands. */
            /* .mb-hero-pack { width: 56% !important; height: auto !important; right: -8% !important; bottom: -4% !important; opacity: .46 !important } */
            /* Same crop as desktop: the pack ends where the card ends. */
            .mb-hero-pack { width: 56% !important; height: auto !important; right: 0 !important; bottom: -4% !important; opacity: .46 !important }
            /* THE HERO MUST BE THE TALLEST BLOCK ON THE PAGE. Measured at
               390px before this: hero 202px, champion card 261px, the other
               two 228px each — so the poster, which is meant to be the loudest
               thing on the screen, was the shortest. A floor of 280px puts it
               back on top, and the pack (a tall figure squeezed into 202px)
               finally has the vertical room it was drawn for.

               The words sit at the FOOT of the poster rather than the top:
               with a floor and no alignment the extra height opened as a hole
               underneath them. The absolutely-positioned pack, gradient and
               halftone are out of flow, so this moves the text only. */
            .mb-hero { min-height: 280px !important; display: flex !important; flex-direction: column !important; justify-content: flex-end !important }
            .mb-hero-pad { padding: 24px 18px 26px !important }
            /* .mb-hero-pad h1 { max-width: 100% !important; font-size: 34px !important } */
            /* Race name dialled back to h3 scale so the hero reads as a label
               over the figures rather than shouting across the whole card. */
            .mb-hero-pad h1 { max-width: 100% !important; font-size: 22px !important }
            /* The three figures as an even three across — wrapping them left
               one stat orphaned on a second row. */
            .mb-hero-facts { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 14px 10px !important; margin-top: 20px !important; align-items: start !important }
            /* .mb-hero-facts > p { grid-column: 1 / -1 !important; margin-bottom: 2px !important } */
            /* The event name left this row for the top of the hero. */
            .mb-hero-event { margin-top: 12px !important }
            .mb-hero-facts .mb-fig-v { font-size: 21px !important }
            .mb-hero-facts .mb-fig-l { font-size: 9px !important; letter-spacing: .16em !important }

            /* The three race figures evenly across, rather than two up and the
               median orphaned underneath with a 48px hole above it. */
            .mb-figures { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 14px 10px !important }
            .mb-figures dd { font-size: 20px !important; margin-top: 7px !important }
            .mb-figures dt { font-size: 9px !important; letter-spacing: .14em !important }

            /* THE TIME COLUMN MUST BE ON SCREEN. At 390px the table's 620px
               floor pushed distance and time out of view behind a sideways
               scroll — on a results page, the finishing time is the one number
               nobody should have to go looking for. Distance gives way; it is
               already in the hero and on the row above. */
            .mb-table { min-width: 0 !important }
            .mb-table th:nth-child(3), .mb-table td:nth-child(3) { display: none }
            .mb-table th:first-child, .mb-table td:first-child { padding-left: 14px !important }

            /* The activities scroll sideways rather than stacking into a
               column that pushes the results off the first screen. */
            .mb-acts { flex-wrap: nowrap !important; overflow-x: auto; scrollbar-width: none }
            .mb-acts::-webkit-scrollbar { display: none }
            /* ONE CARD AT A TIME, AND IT TURNS ITSELF (option B, 2026-11-03).
               Stacked, the three cards ran 744px — most of a screen on their
               own — and the first finisher did not appear until 1,912px, which
               is two and a bit screens down on a results page. As a rail the
               podium costs one card instead of three.

               78%, not 86%: at 86% the next card's edge landed flush against
               the screen edge, so the gap between them was never on screen and
               the rail read as one card butted against a sliver. At 78% the
               second card peeks with clear air either side, which is also what
               tells people it swipes at all.

               The old stack, kept in case the rail is reversed:
               .mb-podium { grid-template-columns: 1fr !important } */
            .mb-podium {
              display: flex !important; gap: 16px !important;
              align-items: stretch !important;
              overflow-x: auto; scroll-snap-type: x mandatory;
              scrollbar-width: none; -webkit-overflow-scrolling: touch;
            }
            .mb-podium::-webkit-scrollbar { display: none }
            .mb-podium > article { flex: 0 0 78% !important; scroll-snap-align: start }
            /* THE CHAMPION LEADS THE RAIL. Desktop orders the cards 2-1-3 so
               the winner stands in the middle; on a rail there is no middle —
               the first card is simply the one you land on, and that has to be
               the winner. Dropping every card back to order 0 restores DOM
               order, which is already 1,2,3. */
            .mb-podium > article { order: 0 !important }
            /* The timer rides ON the card, along its bottom edge, rather than
               under the rail as a row of dots: it says which of the three you
               are on and how long before it turns, in one mark. */
            .mb-podium-track { display: block !important }
            /* The two figures stay SIDE BY SIDE on a phone. Stacked, the card
               became a tall column of white space with one number in it. */
            .mb-tiles { flex-wrap: nowrap !important; gap: 10px !important }
            .mb-tiles > * { flex: 1 1 0 !important; padding: 12px 13px !important }
            .mb-tiles > * span:first-child { font-size: 26px !important }
            .mb-yours { padding: 18px 18px 20px !important }
          }`}
      </style>
      {/* Hero — the poster treatment chosen from the canvas (P4, 2026-11-02).
          An ink-to-espresso ground with the pack standing at full height on the
          right in warm brown, cream display type over it, and the distance cut
          into the headline on a flame plate.

          THE GROUND STAYS DARKEST ON THE LEFT. The gradient over the photograph
          is not decoration — it is what guarantees the headline has something
          solid beneath it whatever the image behind happens to be doing.

          The PUBLISHED badge reads results_published on the race: without it,
          "a fun run where nobody placed" and "not published yet" look alike. */}
      <header
        className="mb-hero"
        style={{
          position: 'relative', overflow: 'hidden', borderRadius: 20, marginBottom: 18,
          background: 'linear-gradient(100deg, #2A1408 0%, #22120A 42%, #150C06 100%)',
          color: '#FAF3E7',
        }}
      >
        <img
          src="/images/race-pack.png"
          alt=""
          className="mb-hero-pack"
          style={{
            // position: 'absolute', right: -18, bottom: 0, height: '100%', maxHeight: 300,
            // right: -18 pushed the pack past the card, so the outermost
            // runner lost his arm to the crop. Flush to the edge keeps him.
            position: 'absolute', right: 0, bottom: 0, height: '100%', maxHeight: 300,
            // Black-and-white art recoloured to the warm brown of the ground,
            // so it sits inside the picture rather than on top of it.
            filter: 'brightness(0) saturate(100%) invert(19%) sepia(58%) saturate(1900%) hue-rotate(2deg) brightness(94%) contrast(96%)',
            opacity: 0.92, pointerEvents: 'none',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(94deg, #2A1408 0%, rgba(42,20,8,.94) 34%, rgba(42,20,8,.30) 62%, rgba(42,20,8,.05) 100%)',
          }}
        />
        {/* A halftone screen, so the pack reads as printed rather than pasted. */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, opacity: 0.45,
            backgroundImage: 'radial-gradient(rgba(250,243,231,.16) 1px, transparent 1.1px)',
            backgroundSize: '6px 6px',
          }}
        />

        <div className="mb-hero-pad" style={{ position: 'relative', padding: '28px 30px 30px' }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, padding: '7px 15px',
              borderRadius: 999, background: ACCENT, color: '#FFF6E9',
              fontSize: 11, fontWeight: 800, letterSpacing: '.22em', textTransform: 'uppercase',
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: '#FFF6E9' }} />
            {/* "Results" rather than "Live results" for an unpublished race:
                results are public as they come in, so "live" said nothing a
                reader could act on and implied the published set was somehow
                less current. The distinction that matters is official or not. */}
            {summary?.results_published ? 'Official results' : 'Results'}
          </span>

          {/* NO PLACEHOLDER TITLE. This used to render the word "Results" the
              instant the page painted and swap it for the real race name a beat
              later, which read as the page changing its mind. Worse, when the
              summary read FAILED the word stayed — the board looked healthy and
              said nothing true. A skeleton says "not yet" honestly, and the
              fallback below only appears once the read has actually settled. */}
          {/* The event the race belongs to, read before its name: it used to
              sit at the foot of the hero beside the figures, where it read as
              a fourth statistic rather than as the heading it is. */}
          {summary?.event_name && (
            <p
              className="mb-hero-event"
              style={{
                margin: '14px 0 0', fontSize: 13, fontWeight: 600,
                letterSpacing: '.04em', color: '#C2AE97',
              }}
            >
              {summary.event_name}
            </p>
          )}

          <h1
            style={{
              // margin: '20px 0 0', fontSize: 'clamp(34px, 6vw, 62px)', fontWeight: 900,
              // margin: '18px 0 0'  — before the event name sat above it.
              // lineHeight: 0.88, letterSpacing: '-.035em', textTransform: 'uppercase',
              // Still the page's h1 for structure, but sized like an h3: the
              // old clamp topped out at 62px and the name dominated the hero.
              margin: '6px 0 0', fontSize: 'clamp(22px, 2.6vw, 30px)', fontWeight: 800,
              lineHeight: 1.08, letterSpacing: '-.02em', textTransform: 'uppercase',
              color: '#FAF3E7', maxWidth: '72%', minHeight: '1.1em',
            }}
          >
            {counts.isPending ? (
              <span
                className="mb-skel"
                aria-label="Loading the race name"
                style={{ display: 'block', width: '68%', height: '0.78em', borderRadius: 10 }}
              />
            ) : (summary?.race_name || 'This race')}
          </h1>

          {/* NOT `counts.isError`. That query catches its own failure and falls
              back to counting the tabs one read at a time, so it SUCCEEDS with
              no summary rather than erroring — and the first version of this
              notice therefore never appeared. What matters to a reader is that
              the name and totals are missing, however that came about. */}
          {!counts.isPending && !summary && (
            <p style={{ margin: '10px 0 0', fontSize: 13, color: '#F2A58D' }}>
              The race name and totals could not be loaded. The results below are complete.
            </p>
          )}

          <div
            className="mb-hero-facts"
            // style={{ display: 'flex', alignItems: 'flex-end', gap: 30, flexWrap: 'wrap', marginTop: 24 }}
            // flex-end lined the figures up with the event name that used to
            // share this row; with the row down to three figures they hang
            // from a common top edge at the left of the hero.
            style={{ display: 'flex', alignItems: 'flex-start', gap: 34, flexWrap: 'wrap', marginTop: 22 }}
          >
            {[
              summary && ['Results', String(summary.everyone), '#FAF3E7'],
              summary && ['Finished', String(summary.completed), '#FAF3E7'],
              summary && Number(summary.completed) > 0 && ['Winning', formatClock(summary.fastest_seconds), '#F2643D'],
            ].filter(Boolean).map(([label, value, colour]) => (
              <div key={label}>
                <div className="mb-fig-l" style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.2em', textTransform: 'uppercase', color: '#9A8873' }}>
                  {label}
                </div>
                <div className="mb-fig-v" style={{ ...mono, marginTop: 5, fontSize: 26, fontWeight: 900, lineHeight: 1, color: colour }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Organizer only, and unlike the attach panel it stays once everyone has
          a result — which is exactly when there is something to publish. */}
      <OrganizerBoardLink
        api={apiHelper}
        eventActivityId={raceId}
        signedIn={user.userId !== null && user.userId !== undefined}
      />

      {/* Organizer only, and only when there is somebody left to attach. For
          everybody else this renders nothing at all. */}
      <OrganizerAttachPanel
        api={apiHelper}
        eventActivityId={raceId}
        signedIn={user.userId !== null && user.userId !== undefined}
      />

      {/* ── The rest of the event ──────────────────────────────────────
          A board is a way into the whole event, not a dead end on one race.
          The race being read is held in ink; the others are a tap away. */}
      {siblings.length > 1 && (
        <nav
          aria-label="Activities in this event"
          className="mb-acts"
          style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}
        >
          {siblings.map((race) => {
            const here = String(race.id) === String(raceId);
            return (
              <Link
                key={race.id}
                to={`/results/${race.id}`}
                aria-current={here ? 'page' : undefined}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 9, minHeight: 46,
                  padding: '0 18px', borderRadius: 999, textDecoration: 'none',
                  border: `1px solid ${here ? INK : HAIR}`,
                  background: here ? INK : '#fff',
                  color: here ? '#FAF3E7' : INK,
                  fontSize: 14, fontWeight: here ? 700 : 500, whiteSpace: 'nowrap',
                  flex: '0 0 auto',
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                  stroke={here ? ACCENT : MUTED} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                >
                  <path d="M13 4a1.6 1.6 0 1 0 0-.1Z" />
                  <path d="M8 21l3-6 4-2-2-5-4 2-2 4" />
                  <path d="M13 8l4 3 3-1" />
                </svg>
                {race.name}
                {here && summary && (
                  <span style={{ ...mono, fontSize: 12, fontWeight: 600, color: 'rgba(250,243,231,.62)' }}>
                    {summary.everyone}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      )}

      {/* ── The podium ─────────────────────────────────────────────────
          The champion is raised and inverted in the middle, second to the
          left and third to the right, the way a podium actually stands. The
          placing numeral sits huge and ghosted behind each card.

          results_published is what GATES this, as of 2026-11-02. Unpublish no
          longer deletes the award rows (it could not: deleting them is what
          made republishing impossible — see publishApi's header), so the rows
          survive a withdrawal and this flag is the only thing standing between
          them and the public board. Drop the check and withdrawn winners
          reappear. */}
      {summary?.results_published && podiumCards.length > 0 && (
        <section
          aria-label="Declared winners"
          ref={railRef}
          className="mb-podium"
          style={{
            display: 'grid', gap: 14, marginBottom: 20, alignItems: 'end',
            gridTemplateColumns: `repeat(auto-fit, minmax(230px, 1fr))`,
          }}
        >
          {podiumCards.map(({ award, result }) => {
            const first = Number(award.position) === 1;
            const label = first ? 'Champion' : (Number(award.position) === 2 ? 'Runner-up' : 'Third');
            return (
              <article
                key={award.id}
                style={{
                  position: 'relative', overflow: 'hidden', borderRadius: 20,
                  padding: first ? '26px 24px 28px' : '20px 22px 22px',
                  background: first ? INK : '#fff',
                  color: first ? '#FAF3E7' : INK,
                  border: `1px solid ${first ? INK : HAIR}`,
                  /* 2nd LEFT, CHAMPION MIDDLE, 3rd RIGHT — the way a podium
                     actually stands, and what the comment above this block has
                     always claimed. The cards arrive sorted 1,2,3, and the old
                     line gave the champion order 0, which put him LEFTMOST: the
                     block read as a plain ranked row, not a podium.
                     Was: order: first ? 0 : Number(award.position), */
                  order: first ? 2 : (Number(award.position) === 2 ? 1 : 3),
                  boxShadow: first ? '0 24px 44px -28px rgba(36,23,8,.55)' : 'none',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    ...mono, position: 'absolute', right: 8, top: -26, fontSize: 132, fontWeight: 800,
                    lineHeight: 1, color: first ? 'rgba(250,243,231,.08)' : 'rgba(36,23,8,.055)',
                  }}
                >
                  {award.position}
                </span>

                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span
                    aria-hidden="true"
                    style={{
                      width: first ? 56 : 48, height: first ? 56 : 48, borderRadius: 999, flex: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: first ? 16 : 14, fontWeight: 700,
                      background: first ? 'rgba(250,243,231,.10)' : '#F4EFEA',
                      color: first ? '#FAF3E7' : '#3B2A14',
                    }}
                  >
                    {initialsOf(award.user_name)}
                  </span>
                  <span
                    style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase',
                      padding: '5px 11px', borderRadius: 999,
                      background: first ? ACCENT : '#FFF1E8',
                      color: first ? '#FFF6E9' : ACCENT,
                    }}
                  >
                    {label}
                  </span>
                </div>

                <div style={{ position: 'relative', marginTop: 14, fontSize: first ? 24 : 20, fontWeight: 700, letterSpacing: '-.02em' }}>
                  {award.user_name}
                </div>
                <div style={{ ...mono, position: 'relative', marginTop: 5, fontSize: 12, color: first ? 'rgba(250,243,231,.64)' : MUTED }}>
                  {[result?.bib && `Bib ${result.bib}`, award.category || 'Overall'].filter(Boolean).join(' · ')}
                </div>

                {/* ALWAYS DRAWN, even when the time is not to hand.
                    This block used to render only `if (result)`, and a winner
                    whose result row had not been loaded got a card one block
                    shorter than the others — the podium then sat visibly out of
                    line, which is how the 10-row cap on the board's own list
                    first showed itself: the champion placed 21st on time, was
                    not in the first ten rows, and lost his time while 2nd and
                    3rd kept theirs. The cap is fixed, and so is this: a podium
                    is three cards in a row and must look like three cards in a
                    row whatever is missing. */}
                <div
                  style={{
                    position: 'relative', marginTop: 16, paddingTop: 14,
                    borderTop: `1px solid ${first ? 'rgba(250,243,231,.16)' : HAIR}`,
                    display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                    minHeight: first ? 44 : 38,
                  }}
                >
                  {result ? (
                    <>
                      <span style={{ ...mono, fontSize: first ? 30 : 25, fontWeight: 700, letterSpacing: '-.03em', color: first ? ACCENT : INK }}>
                        {formatClock(result.duration_seconds)}
                      </span>
                      <span style={{ ...mono, fontSize: 12, color: first ? 'rgba(250,243,231,.64)' : MUTED }}>
                        {formatPace(result.duration_seconds, result.distance_meters)} /km
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: 13, color: first ? 'rgba(250,243,231,.64)' : MUTED }}>
                      Time not available
                    </span>
                  )}
                </div>

                {/* The rail's timer, drawn on the card itself and hidden by CSS
                    on desktop where there is nothing to count down. The fill is
                    scaled by the rotation hook, never by a CSS animation: the
                    bar has to stop where it stood when a thumb lands on the
                    card, and a keyframe cannot be paused mid-flight and resumed
                    from the same place. */}
                <span
                  aria-hidden="true"
                  className="mb-podium-track"
                  style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0, height: 3,
                    background: first ? 'rgba(250,243,231,.14)' : 'rgba(36,23,8,.10)',
                  }}
                >
                  <span
                    data-podium-fill=""
                    style={{
                      display: 'block', height: '100%', width: '100%',
                      transform: 'scaleX(0)', transformOrigin: 'left center',
                      background: first ? '#FB7A3C' : ACCENT,
                    }}
                  />
                </span>
              </article>
            );
          })}
        </section>
      )}

      {/* Your result.
          Two figures in tiles rather than a column of labels: on a phone the
          old card was a name, one lonely time and a lot of nothing, because
          POSITION is empty until a race is ranked. When there is no position
          the second tile carries the distance instead, so the pair is always
          balanced and the card never looks half-built.

          The bar only appears when there is a field to be ahead of. */}
      {mine.data && (
        <section
          aria-label="Your result"
          className="mb-yours"
          style={{
            background: '#fff', border: '1px solid rgba(255,106,26,.30)', borderRadius: 22,
            padding: '22px 24px 24px', marginBottom: 18,
            boxShadow: '0 1px 2px rgba(36,23,8,.04), 0 16px 36px -22px rgba(255,106,26,.40)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 10px', borderRadius: 7, background: ACCENT, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.12em' }}>
              YOUR RESULT
            </span>
            {/* "Ahead of 100%" is a strange way to tell somebody they won.
                The podium gets the word; everyone else gets the share of the
                field they came ahead of. */}
            {Number(mine.data.position_overall) >= 1 && Number(mine.data.position_overall) <= 3 ? (
              <span style={{ ...mono, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 999, background: '#FFF1E8', color: ACCENT }}>
                {PODIUM_WORD[Number(mine.data.position_overall)]}
              </span>
            ) : ahead !== null && (
              <span style={{ ...mono, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 999, background: '#FFF1E8', color: ACCENT }}>
                Ahead of {ahead}%
              </span>
            )}
          </div>

          <div style={{ marginTop: 12, fontSize: 24, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.12, color: INK }}>
            {mine.data.athlete_name || user.name}
          </div>
          <div style={{ ...mono, marginTop: 5, fontSize: 12, color: MUTED }}>
            {[mine.data.bib && `Bib ${mine.data.bib}`, summary?.race_name].filter(Boolean).join(' · ')}
          </div>

          <div className="mb-tiles" style={{ display: 'flex', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
            {[
              mine.data.position_overall
                ? { label: 'Position', value: String(mine.data.position_overall), note: summary?.completed ? `of ${summary.completed}` : '', accent: true }
                : { label: 'Distance', value: formatKm(mine.data.distance_meters), note: '', accent: false },
              { label: 'Finish time', value: formatClock(mine.data.duration_seconds), note: `${formatPace(mine.data.duration_seconds, mine.data.distance_meters)} /km`, accent: false },
            ].map((tile) => (
              <div
                key={tile.label}
                style={{
                  flex: '1 1 150px', minWidth: 0, padding: '14px 16px', borderRadius: 14,
                  background: tile.accent ? '#FFF1E8' : '#FAF7F4',
                  border: `1px solid ${tile.accent ? 'rgba(194,65,12,.22)' : HAIR}`,
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED }}>
                  {tile.label}
                </div>
                <div style={{ ...mono, marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, letterSpacing: '-.035em', color: tile.accent ? ACCENT : INK }}>
                    {tile.value}
                  </span>
                  {tile.note && <span style={{ fontSize: 12, color: MUTED }}>{tile.note}</span>}
                </div>
              </div>
            ))}
          </div>

          {ahead !== null && (
            <div style={{ marginTop: 16 }}>
              <div style={{ height: 8, borderRadius: 999, background: '#F4EFEA', overflow: 'hidden' }}>
                <div style={{ width: `${ahead}%`, height: '100%', borderRadius: 999, background: ACCENT }} />
              </div>
              <p style={{ ...mono, margin: '8px 0 0', fontSize: 12, color: MUTED }}>
                {mine.data.position_overall} of {summary.completed} finishers
                {formatKm(mine.data.distance_meters) && ` · ${formatKm(mine.data.distance_meters)} recorded`}
              </p>
            </div>
          )}
        </section>
      )}

      {/* The race in three figures, in a card like everything else on the page.
          These used to sit bare on the background with a sentence above them,
          which made the middle of the page look like a draft. */}
      {summary && Number(summary.completed) > 0 && (
        <section
          aria-label="This race in figures"
          style={{
            background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 22,
            padding: '22px 28px', marginBottom: 20,
          }}
        >
          <dl className="mb-figures" style={{ display: 'flex', gap: 48, flexWrap: 'wrap', margin: 0 }}>
            {[
              ['Finishers', String(summary.completed)],
              ['Winning time', formatClock(summary.fastest_seconds)],
              ['Median time', formatClock(summary.median_seconds)],
            ].map(([label, value]) => (
              <div key={label}>
                <dt style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, whiteSpace: 'nowrap' }}>
                  {label}
                </dt>
                <dd style={{ ...mono, margin: '10px 0 0', fontSize: 28, fontWeight: 600, letterSpacing: '-.03em', lineHeight: 1, color: INK }}>
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p style={{ margin: '16px 0 0', fontSize: 13, color: MUTED }}>
            Every result for this race. Sorting happens inside a tab, so a withdrawal can never sit above a finisher.
          </p>
        </section>
      )}

      {/* The shape of the field. Held back below a handful of finishers: three
          bars of height one say nothing a reader cannot already see in the
          table, and a chart that says nothing still asks to be read. */}
      {summary && Number(summary.completed) >= HISTOGRAM_MIN_FINISHERS && (
        <FinishSpread
          bars={histogramBars(bands.data, { mineSeconds: mine.data?.duration_seconds })}
          mineResult={mine.data}
          summary={summary}
        />
      )}

      {/* Find yourself — direction A's band. A results list is read for one
          name, and on a field of a thousand that name is four pages down. The
          count says why this is here rather than leaving it to be guessed. */}
      <section
        aria-label="Find yourself in the field"
        style={{
          background: '#FFF1E8', border: '1px solid rgba(255,106,26,.22)', borderRadius: 18,
          padding: '18px 22px', marginBottom: 18, display: 'flex', alignItems: 'center',
          gap: 18, flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: INK }}>Find yourself in the field</h2>
          <p style={{ ...mono, margin: '4px 0 0', fontSize: 13, color: '#7A5C3C' }}>
            {sorted.length} {sorted.length === 1 ? 'result' : 'results'}
            {pageCount > 1 && <> across {pageCount} pages — don&rsquo;t scroll for it</>}
          </p>
        </div>

        <form onSubmit={onSearch} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: '1 1 320px' }}>
          <div style={{ flex: '1 1 220px', minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7A5C3C"
              strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"
              style={{ position: 'absolute', left: 14 }}
            >
              <circle cx="11" cy="11" r="6.5" />
              <path d="M16 16l4 4" />
            </svg>
            <label htmlFor="board-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
              Search by name or bib number
            </label>
            <input
              id="board-search"
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                // Emptying the box — including by the input's own X — puts the
                // whole field back at once. Waiting for Search to be pressed
                // left the visitor looking at a filtered list they had cleared.
                if (e.target.value === '') {
                  setTerm('');
                  setPage(1);
                }
              }}
              placeholder="Name or bib number"
              style={{
                width: '100%', minHeight: 46, padding: '0 14px 0 40px', borderRadius: 12,
                border: '1px solid rgba(36,23,8,.16)', background: '#fff', fontSize: 14,
                color: INK, boxSizing: 'border-box',
              }}
            />
          </div>
          <button
            type="submit"
            style={{
              minHeight: 46, padding: '0 20px', border: 0, borderRadius: 12, background: INK,
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', flex: 'none',
            }}
          >
            Search
          </button>
        </form>
      </section>

      {/* Tabs and table in one card, as the design draws them: the tab strip on
          its own warm ground, the table flush inside the same rounded box. */}
      <section style={{ background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 22, overflow: 'hidden' }}>
      {/* One row, always. Wrapping put the tabs on two lines on a phone, which
          pushed the table down and made the card look broken. They scroll
          sideways instead — the usual phone pattern, and the scrollbar is
          hidden because the tabs themselves show there is more to the right. */}
      <div
        role="tablist"
        aria-label="Result status"
        className="mb-tabstrip"
        style={{
          display: 'flex', gap: 4, flexWrap: 'nowrap', overflowX: 'auto',
          padding: '10px 12px 0', borderBottom: `1px solid ${HAIR}`, background: '#FAF7F4',
          scrollbarWidth: 'none',
        }}
      >
        {tabs.map((t) => {
          const on = t.key === tabKey;
          const count = tabCounts[t.key];
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => pickTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 16px',
                flex: '0 0 auto', whiteSpace: 'nowrap',
                border: 0, borderBottom: `3px solid ${on ? ACCENT : 'transparent'}`,
                borderRadius: '10px 10px 0 0',
                background: on ? '#fff' : 'none', color: on ? INK : MUTED,
                fontSize: 14, fontWeight: on ? 700 : 500, cursor: 'pointer',
              }}
            >
              {t.label}
              {count !== null && count !== undefined && (
                <span style={{ ...mono, fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 999, background: on ? '#FFF1E8' : 'rgba(36,23,8,.06)', color: on ? ACCENT : MUTED }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading, error and empty are three different things and used to be
          one. On a failed read the loader span forever: no message, no retry,
          nothing to tell a visitor the page had given up. */}
      {results.isPending && !results.isError && <BoardLoader />}

      {results.isError && (
        <section
          role="alert"
          style={{
            border: `1px solid rgba(194,65,12,.3)`, background: '#FFF1E8', borderRadius: 18,
            padding: '22px 24px', marginBottom: 18,
          }}
        >
          <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#8A2C06' }}>
            These results could not be loaded.
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#8A2C06', lineHeight: 1.6 }}>
            The race is there — the list of finishers did not come back. This is
            usually a connection that dropped.
          </p>
          <button
            type="button"
            onClick={() => results.refetch()}
            disabled={results.isFetching}
            style={{
              marginTop: 16, minHeight: 46, padding: '0 20px', border: 0, borderRadius: 11,
              background: results.isFetching ? '#D8CEC3' : INK, color: '#fff',
              fontSize: 14, fontWeight: 700, cursor: results.isFetching ? 'not-allowed' : 'pointer',
            }}
          >
            {results.isFetching ? 'Trying again…' : 'Try again'}
          </button>
        </section>
      )}
      {results.isError && (
        <p role="alert" style={{ fontSize: 14, color: ACCENT, padding: '24px 2px' }}>
          Could not load the results for this race. Try again in a moment.
        </p>
      )}

      {!results.isPending && !results.isError && sorted.length === 0 && (
        <div style={{ padding: '56px 24px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: INK }}>{empty.title}</p>
          <p style={{ margin: '8px auto 0', maxWidth: 440, fontSize: 14, lineHeight: 1.6, color: MUTED }}>{empty.body}</p>
        </div>
      )}

      {!results.isPending && !results.isError && sorted.length > 0 && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className="mb-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <thead>
                <tr>
                  <th scope="col" style={{ ...th, width: 74 }}>#</th>
                  <th scope="col" style={th}>Name</th>
                  {[{ key: 'distance_meters', label: 'Distance' }, { key: 'duration_seconds', label: 'Time' }].map((col) => {
                    const on = canSort && activeSort.column === col.key;
                    return (
                      <th
                        key={col.key}
                        scope="col"
                        aria-sort={on ? (activeSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                        style={{ ...th, textAlign: 'right', width: 150 }}
                      >
                        {canSort ? (
                          <button
                            type="button"
                            onClick={() => pickSort(col.key)}
                            style={{ minHeight: 44, border: 0, background: 'none', cursor: 'pointer', font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', color: on ? ACCENT : MUTED }}
                          >
                            {col.label}{on ? (activeSort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                          </button>
                        ) : col.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visible.map((row, index) => {
                  // Your own row, marked where the eye actually goes. The card
                  // above and the histogram already say "this is you", but a
                  // reader scanning the field reads the TABLE, and there every
                  // row looked the same — so people scrolled past themselves.
                  const isMine = mine.data && String(row.id) === String(mine.data.id);
                  return (
                  <tr key={row.id} style={isMine ? { background: '#FFF1E8' } : undefined}>
                    <td style={{ ...td, ...mono, fontWeight: 600, color: MUTED }}>
                      {canSort ? (safePage - 1) * ROWS_PER_PAGE + index + 1 : '—'}
                    </td>
                    {/* The name is the way into the run behind the result —
                        its route, splits and climb. A link in the cell rather
                        than a click handler on the <tr>: a row is not
                        focusable, so a handler there would be unreachable by
                        keyboard and invisible to a screen reader. */}
                    <td style={{ ...td, color: INK, fontSize: 15 }}>
                      <Link
                        to={`/results/${eventActivityId}/${row.id}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 8,
                          minHeight: 44, color: INK, fontWeight: 600, textDecoration: 'none',
                        }}
                      >
                        {row.athlete_name || 'Athlete'}
                        <span aria-hidden="true" style={{ color: ACCENT, fontWeight: 700 }}>›</span>
                      </Link>
                      {isMine && (
                        <span style={{
                          marginLeft: 8, padding: '3px 8px', borderRadius: 7, background: ACCENT,
                          color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.1em',
                        }}
                        >
                          YOU
                        </span>
                      )}
                      {row.bib ? <span style={{ ...mono, color: MUTED, fontSize: 12, marginLeft: 8 }}>Bib {row.bib}</span> : null}
                    </td>
                    <td style={{ ...td, ...mono, textAlign: 'right', color: '#3B2A14' }}>{formatKm(row.distance_meters)}</td>
                    <td style={{ ...td, ...mono, textAlign: 'right', fontWeight: 600, color: INK }}>{formatClock(row.duration_seconds)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Only when your row is NOT already on screen. Pinning it regardless
              put the winner's own row underneath the four people they had just
              beaten, which reads as a fifth finisher. The point of the pin is to
              save you hunting through pages for yourself — if you are right
              there, it has nothing to do. */}
          {mine.data && !visible.some((row) => String(row.id) === String(mine.data.id)) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 14px', height: 68, background: '#FFF1E8', borderTop: `2px solid ${ACCENT}`, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', padding: '4px 9px', borderRadius: 7, background: ACCENT, color: '#fff' }}>YOU</span>
              <span style={{ flex: 1, minWidth: 120, fontSize: 15, fontWeight: 700, color: INK }}>
                {mine.data.athlete_name || user.name}
              </span>
              <span style={{ ...mono, fontSize: 14, color: '#3B2A14' }}>{formatKm(mine.data.distance_meters)}</span>
              <span style={{ ...mono, fontSize: 17, fontWeight: 700, color: ACCENT }}>{formatClock(mine.data.duration_seconds)}</span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '16px 22px', flexWrap: 'wrap', borderTop: `1px solid ${HAIR}`, background: '#FAF7F4' }}>
            <p style={{ ...mono, margin: 0, fontSize: 13, color: MUTED }}>
              Showing {(safePage - 1) * ROWS_PER_PAGE + 1}–{Math.min(safePage * ROWS_PER_PAGE, sorted.length)} of {sorted.length}
              {results.data?.truncated ? ' (first 5,000)' : ''}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                style={{ minHeight: 44, padding: '0 16px', borderRadius: 11, border: `1px solid ${HAIR}`, background: '#fff', color: safePage <= 1 ? '#A69683' : INK, cursor: safePage <= 1 ? 'not-allowed' : 'pointer' }}>
                Previous
              </button>
              <button type="button" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={safePage >= pageCount}
                style={{ minHeight: 44, padding: '0 16px', borderRadius: 11, border: 0, background: safePage >= pageCount ? '#D8CEC3' : INK, color: '#fff', fontWeight: 700, cursor: safePage >= pageCount ? 'not-allowed' : 'pointer' }}>
                Next
              </button>
            </div>
          </div>
        </>
      )}
      </section>
    </main>
  );
}
